import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Copy, CheckCircle, AlertCircle, Database } from 'lucide-react';
import { adminAuth } from '../services/adminAuth';
import { useNavigate } from 'react-router-dom';

const DatabaseSetup: React.FC = () => {
  const navigate = useNavigate();
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [copied, setCopied] = useState(false);

  const sqlScript = `-- Create campaigns table and related functions
-- This should be run directly in the Supabase SQL editor

-- Create campaigns table to manage different campaign types
CREATE TABLE IF NOT EXISTS campaigns (
    id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
    created_at TIMESTAMP WITH TIME ZONE DEFAULT timezone('utc'::text, now()) NOT NULL,
    updated_at TIMESTAMP WITH TIME ZONE DEFAULT timezone('utc'::text, now()) NOT NULL,
    
    -- Campaign Details
    name TEXT NOT NULL,
    campaign_type TEXT NOT NULL CHECK (campaign_type IN ('buyers_agent_outreach', 'customer_outreach')),
    campaign_subtype TEXT NOT NULL CHECK (campaign_subtype IN ('closing_anniversary_agent', 'recently_closed_agent', 'recently_closed_customer')),
    
    -- Request Configuration
    request_url TEXT NOT NULL,
    source_platform TEXT NOT NULL CHECK (source_platform IN ('homes.com', 'zillow')),
    
    -- Campaign Status
    status TEXT DEFAULT 'active' CHECK (status IN ('active', 'paused', 'completed', 'archived')),
    
    -- Scraping Configuration
    max_pages INTEGER DEFAULT 1,
    scraping_interval_hours INTEGER DEFAULT 24,
    last_scraped_at TIMESTAMP WITH TIME ZONE,
    
    -- Statistics
    total_properties_found INTEGER DEFAULT 0,
    total_agents_contacted INTEGER DEFAULT 0,
    total_customers_contacted INTEGER DEFAULT 0,
    
    -- Screenshot thumbnail URL field
    screenshot_thumbnail_url TEXT,
    
    -- Admin and Management
    created_by_admin_id TEXT,
    notes TEXT,
    
    -- Ensure unique campaign names
    UNIQUE(name)
);

-- Add comment to document the screenshot field
COMMENT ON COLUMN campaigns.screenshot_thumbnail_url IS 'URL of screenshot captured from the source website to use as campaign thumbnail';

-- Create indexes for better query performance
CREATE INDEX IF NOT EXISTS idx_campaigns_status ON campaigns(status);
CREATE INDEX IF NOT EXISTS idx_campaigns_campaign_type ON campaigns(campaign_type);
CREATE INDEX IF NOT EXISTS idx_campaigns_created_at ON campaigns(created_at);

-- Create a function to update the updated_at timestamp for campaigns
CREATE OR REPLACE FUNCTION update_campaigns_updated_at()
RETURNS TRIGGER AS $$
BEGIN
    NEW.updated_at = timezone('utc'::text, now());
    RETURN NEW;
END;
$$ language 'plpgsql';

-- Create the trigger
DROP TRIGGER IF EXISTS update_campaigns_updated_at ON campaigns;
CREATE TRIGGER update_campaigns_updated_at
    BEFORE UPDATE ON campaigns
    FOR EACH ROW
    EXECUTE FUNCTION update_campaigns_updated_at();

-- Add RLS (Row Level Security) policies for campaigns
ALTER TABLE campaigns ENABLE ROW LEVEL SECURITY;

-- Drop existing policies if they exist
DROP POLICY IF EXISTS "Allow authenticated read access" ON campaigns;
DROP POLICY IF EXISTS "Allow authenticated insert access" ON campaigns;
DROP POLICY IF EXISTS "Allow authenticated update access" ON campaigns;
DROP POLICY IF EXISTS "Allow admin delete access" ON campaigns;

-- Allow authenticated users to read all campaigns
CREATE POLICY "Allow authenticated read access" ON campaigns
    FOR SELECT USING (auth.role() = 'authenticated');

-- Allow authenticated users to insert campaigns
CREATE POLICY "Allow authenticated insert access" ON campaigns
    FOR INSERT WITH CHECK (auth.role() = 'authenticated');

-- Allow authenticated users to update campaigns
CREATE POLICY "Allow authenticated update access" ON campaigns
    FOR UPDATE USING (auth.role() = 'authenticated');

-- Allow admin delete access for campaigns
CREATE POLICY "Allow admin delete access" ON campaigns
    FOR DELETE USING (auth.role() = 'authenticated');`;

  const testDatabase = async () => {
    if (!adminAuth.isAuthenticated()) {
      setTestResult('Please log in as admin first');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      // Test basic connection
      const { data: connectionTest, error: connectionError } = await supabase
        .from('campaigns')
        .select('count', { count: 'exact', head: true });

      if (connectionError) {
        if (connectionError.message?.includes('relation "campaigns" does not exist')) {
          setTestResult('❌ The campaigns table does not exist. Please run the SQL script below in your Supabase SQL editor.');
        } else {
          setTestResult(`❌ Database error: ${connectionError.message}`);
        }
      } else {
        setTestResult('✅ Campaigns table exists and is accessible!');
      }
    } catch (error) {
      setTestResult(`❌ Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(sqlScript);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Database className="w-8 h-8" />
            Database Setup
          </h1>
          <p className="text-gray-600">Fix the campaigns table issue</p>
        </div>

        {/* Test Database Connection */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Database Connection</h2>
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={testDatabase}
              disabled={testing}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {testing ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Database className="w-4 h-4" />
              )}
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          </div>

          {testResult && (
            <div className={`p-4 rounded-lg ${
              testResult.includes('✅') 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <p className={`text-sm ${
                testResult.includes('✅') ? 'text-green-700' : 'text-red-700'
              }`}>
                {testResult}
              </p>
            </div>
          )}
        </div>

        {/* SQL Script */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Create Campaigns Table</h2>
            <button
              onClick={copyToClipboard}
              className="flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {copied ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy SQL'}
            </button>
          </div>

          <div className="mb-4">
            <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Copy the SQL script below</li>
                  <li>Go to your <a href="https://supabase.com/dashboard/project/qrmzfkvnksglemsiaxnp/sql" target="_blank" rel="noopener noreferrer" className="underline font-medium">Supabase SQL Editor</a></li>
                  <li>Paste and run the script</li>
                  <li>Come back and test the connection</li>
                  <li>Try creating a campaign again</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
            <pre className="text-sm whitespace-pre-wrap">{sqlScript}</pre>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-8 flex gap-4">
          <button
            onClick={() => navigate('/admin/campaigns')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
          >
            Back to Campaigns
          </button>
          <button
            onClick={() => navigate('/admin')}
            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          >
            Back to Admin
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatabaseSetup; 